import {
  Activity,
  ArrowRight,
  BookOpen,
  Braces,
  Check,
  ChevronDown,
  Copy,
  Database,
  Eye,
  ExternalLink,
  KeyRound,
  Network,
  ShieldCheck,
  Sparkles,
  TerminalSquare,
  Wrench
} from "lucide-react";
import { useState, type ReactNode } from "react";

const baseUrl = "https://api.akiomae.xyz";
const documentationSections = [
  ["Introducción", "#introduction"],
  ["Inicio rápido", "#quickstart"],
  ["Autenticación", "#authentication"],
  ["Endpoints", "#endpoints"],
  ["Professional", "#professional"],
  ["Easy", "#easy"],
  ["Modos", "#modes"],
  ["Orquestación", "#routing"],
  ["Memoria", "#memory"],
  ["Visión", "#vision"],
  ["Artefactos", "#artifacts"],
  ["Streaming", "#streaming"],
  ["JSON estructurado", "#structured"],
  ["Skills y prompts", "#skills"],
  ["Conectores", "#connectors"],
  ["Modo agéntico", "#agentic"],
  ["Generaciones", "#generations"],
  ["BYOK", "#byok"],
  ["Modelos HTTP", "#custom-models"],
  ["Respuesta", "#response"],
  ["Errores", "#errors"],
  ["Scopes", "#scopes"],
  ["Límites y seguridad", "#limits"],
  ["Webhooks y SDK", "#operations"]
] as const;

const professionalCurl = `curl ${baseUrl}/api/v1/responses \\
  -H "Authorization: Bearer $AK_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "ak-auto",
    "mode": "standard",
    "messages": [
      {
        "role": "system",
        "content": "Responde como analista empresarial."
      },
      {
        "role": "user",
        "content": "Resume estas métricas y devuelve próximos pasos."
      }
    ],
    "stream": false
  }'`;

const easyCurl = `curl ${baseUrl}/api/v1/easy \\
  -H "Authorization: Bearer $AK_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "prompt": "Redacta una propuesta comercial",
    "context": "Consultora B2B de automatización",
    "personality": "Profesional, directa y cordial",
    "name": "AK",
    "genero": "neutral",
    "lang": "Español",
    "mode": "standard",
    "settings": {
      "memory": true,
      "ui_blocks": true
    }
  }'`;

const javascriptExample = `const response = await fetch(
  "${baseUrl}/v1/chat/completions",
  {
    method: "POST",
    headers: {
      "Authorization": \`Bearer \${process.env.AK_API_KEY}\`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      model: "ak-auto",
      messages: [
        { role: "user", content: "Explica el informe adjunto" }
      ],
      stream: false
    })
  }
);

if (!response.ok) {
  const problem = await response.json();
  throw new Error(problem.error?.message ?? "AK request failed");
}

const completion = await response.json();
console.log(completion.choices[0].message.content);`;

const pythonExample = `import os
import requests

response = requests.post(
    "${baseUrl}/api/v1/responses",
    headers={
        "Authorization": f"Bearer {os.environ['AK_API_KEY']}",
        "Content-Type": "application/json",
    },
    json={
        "model": "ak-auto",
        "input": {
            "customer": "Akioma",
            "task": "Create an onboarding checklist",
        },
        "response_format": {"type": "json_object"},
    },
    timeout=60,
)
response.raise_for_status()
print(response.json()["output_text"])`;

const agenticExample = `{
  "model": "ak-auto",
  "mode": "agentic",
  "input": "Investiga el clima y la hora actual de Lima y prepara una agenda.",
  "tools": ["world_weather", "world_time"],
  "tool_choice": "required",
  "skills": ["executive-summary"],
  "stream": false
}`;

const structuredExample = `{
  "model": "ak-auto",
  "input": "Extrae empresa, contacto y prioridad del texto...",
  "response_format": {
    "type": "json_schema",
    "json_schema": {
      "name": "lead",
      "schema": {
        "type": "object",
        "properties": {
          "empresa": { "type": "string" },
          "contacto": { "type": "string" },
          "prioridad": {
            "enum": ["alta", "media", "baja"]
          }
        },
        "required": ["empresa", "contacto", "prioridad"],
        "additionalProperties": false
      }
    }
  }
}`;

const responseExample = `{
  "object": "ak.response",
  "output_text": "Respuesta final...",
  "provider": "provider-route",
  "model": "managed-route",
  "mode": "standard",
  "thread_id": "80e92f1a-...",
  "usage": {
    "input_tokens": 142,
    "output_tokens": 386,
    "total_tokens": 528,
    "credits": 0.5,
    "billing_source": "system"
  },
  "tool_calls": [],
  "ui_blocks": [],
  "artifacts": [],
  "citations": [],
  "latency_ms": 842,
  "request_id": "1c2db6d5-..."
}`;

const errorExample = `{
  "error": {
    "code": "credits_exhausted",
    "message": "No quedan créditos disponibles para hoy.",
    "request_id": "c4836cd0-..."
  }
}`;

function DocsBrand() {
  return (
    <a className="public-docs-brand" href="/">
      <span>AK</span>
      <div><strong>AK-v5.0</strong><small>Developer Documentation</small></div>
    </a>
  );
}

function CodeBlock({ children, language = "json" }: {
  children: string;
  language?: string;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_500);
    } catch {
      setCopied(false);
    }
  }
  return (
    <div className="public-code-block">
      <div><span>{language}</span><button onClick={copy}>{copied ? <Check size={14} /> : <Copy size={14} />}{copied ? "Copiado" : "Copiar"}</button></div>
      <pre><code>{children}</code></pre>
    </div>
  );
}

function Endpoint({ method, path, children }: {
  method: string;
  path: string;
  children: ReactNode;
}) {
  return (
    <div className="public-endpoint">
      <span>{method}</span><code>{path}</code><p>{children}</p>
    </div>
  );
}

function ParameterTable({ rows }: {
  rows: Array<[string, string, string, string]>;
}) {
  return (
    <div className="public-parameter-table">
      <div className="public-parameter-head"><span>Campo</span><span>Tipo</span><span>Predeterminado</span><span>Descripción</span></div>
      {rows.map(([name, type, fallback, description]) => (
        <div className="public-parameter-row" key={name}>
          <code>{name}</code><span>{type}</span><span>{fallback}</span><p>{description}</p>
        </div>
      ))}
    </div>
  );
}

export function PublicDocumentation() {
  return (
    <div className="public-docs-page">
      <header className="public-docs-header">
        <DocsBrand />
        <nav>
          <a className="public-docs-header-link" href="/status"><Activity size={15} /> Estado</a>
          <a className="public-docs-header-action" href="/auth/google">Dashboard <ArrowRight size={15} /></a>
        </nav>
      </header>

      <aside className="public-docs-sidebar">
        <div className="public-docs-sidebar-title">Documentación</div>
        <nav aria-label="Secciones de documentación">
          {documentationSections.map(([label, href]) => <a href={href} key={href}>{label}</a>)}
        </nav>
      </aside>

      <main className="public-docs-main">
        <details className="public-docs-mobile-nav">
          <summary>Explorar documentación <ChevronDown size={16} /></summary>
          <nav>
            {documentationSections.map(([label, href]) => <a href={href} key={href}>{label}</a>)}
          </nav>
        </details>
        <section className="public-docs-hero" id="introduction">
          <div className="public-docs-eyebrow"><BookOpen size={14} /> Documentación pública · API v1</div>
          <h1>Construye sobre AK-v5.0</h1>
          <p>Una referencia completa para integrar el orquestador, mantener memoria, ejecutar herramientas, usar claves propias y recibir respuestas compatibles con aplicaciones modernas.</p>
          <div className="public-docs-hero-actions">
            <a href="#quickstart">Empezar ahora <ArrowRight size={16} /></a>
            <a href="/status">Comprobar servicios <Activity size={16} /></a>
          </div>
          <div className="public-docs-base"><span>Base URL</span><code>{baseUrl}</code></div>
        </section>

        <section className="public-docs-section" id="quickstart">
          <div className="public-docs-section-icon"><TerminalSquare /></div>
          <h2>Inicio rápido</h2>
          <p>Necesitas una API key AK creada desde el dashboard. Envíala como Bearer token, usa JSON en el cuerpo y conserva el <code>request_id</code> de cualquier respuesta o error.</p>
          <CodeBlock language="bash">{professionalCurl}</CodeBlock>
          <div className="public-docs-callout success"><Check size={18} /><div><strong>Contrato recomendado</strong><p>Para integraciones nuevas usa <code>/api/v1/responses</code>. Si ya utilizas un cliente compatible con Chat Completions, empieza con <code>/v1/chat/completions</code>.</p></div></div>
          <h3>JavaScript</h3>
          <CodeBlock language="javascript">{javascriptExample}</CodeBlock>
          <h3>Python</h3>
          <CodeBlock language="python">{pythonExample}</CodeBlock>
        </section>

        <section className="public-docs-section" id="authentication">
          <div className="public-docs-section-icon"><KeyRound /></div>
          <h2>Autenticación</h2>
          <p>Las integraciones usan claves AK con prefijo propio. El secreto completo se muestra una sola vez al crearlo y en la base de datos se almacena únicamente su hash.</p>
          <CodeBlock language="http">{`Authorization: Bearer ak-live-...`}</CodeBlock>
          <ul>
            <li>No envíes claves en query strings ni URLs.</li>
            <li>Crea una clave diferente por aplicación o entorno.</li>
            <li>Concede únicamente los scopes que realmente utilizará.</li>
            <li>Revoca la clave desde el dashboard si se expone.</li>
          </ul>
        </section>

        <section className="public-docs-section" id="endpoints">
          <div className="public-docs-section-icon"><Network /></div>
          <h2>Endpoints principales</h2>
          <Endpoint method="POST" path="/api/v1/responses">Contrato nativo con toda la metadata, herramientas, memoria, artefactos y citas.</Endpoint>
          <Endpoint method="POST" path="/v1/chat/completions">Compatibilidad con el formato de Chat Completions.</Endpoint>
          <Endpoint method="POST" path="/api/v1/easy">Contrato guiado con contexto, personalidad, nombre, género e idioma.</Endpoint>
          <Endpoint method="GET" path="/api/v1/models">Aliases y rutas disponibles para la cuenta.</Endpoint>
          <Endpoint method="GET" path="/api/v1/skills">Catálogo de skills visibles.</Endpoint>
          <Endpoint method="GET" path="/api/v1/connectors">Herramientas permitidas por el plan.</Endpoint>
          <Endpoint method="GET" path="/api/v1/threads">Registros privados de memoria.</Endpoint>
          <Endpoint method="POST" path="/api/v1/files/vision">Sube una imagen privada mediante multipart/form-data.</Endpoint>
          <Endpoint method="GET" path="/api/v1/artifacts">Artefactos reutilizables y versionados.</Endpoint>
          <Endpoint method="GET" path="/openapi.json">Contrato OpenAPI 3.1 público.</Endpoint>
          <Endpoint method="GET" path="/api/v1/usage">Consumo, tokens, latencia, créditos y BYOK.</Endpoint>
        </section>

        <section className="public-docs-section" id="professional">
          <div className="public-docs-section-icon"><Braces /></div>
          <h2>Contrato Professional</h2>
          <p>Es el contrato predeterminado. Acepta un array <code>messages</code> al estilo OpenAI o una entrada libre mediante <code>input</code> o <code>prompt</code>. Un objeto o array JSON se conserva como dato estructurado.</p>
          <ParameterTable rows={[
            ["model", "string", "ak-auto", "Alias lógico utilizado por el orquestador."],
            ["messages", "array", "—", "Mensajes system, developer, user y assistant."],
            ["input", "any", "—", "Texto, objeto o array de entrada."],
            ["mode", "string", "auto", "auto, fast, standard, thinking o agentic."],
            ["temperature", "number", "administrado", "Variación de la salida cuando la ruta lo admite."],
            ["top_p", "number", "administrado", "Muestreo nucleus opcional."],
            ["max_output_tokens", "integer", "4096", "Máximo solicitado para la salida."],
            ["skills", "string[]", "[]", "Slugs de instrucciones reutilizables."],
            ["tools", "string[]", "[]", "Identificadores de conectores permitidos."],
            ["tool_choice", "string", "auto", "auto, none o required."],
            ["thread_id", "uuid", "—", "Memoria existente que se continuará."],
            ["artifact_ids", "uuid[]", "[]", "Artefactos privados que se importarán como contexto."],
            ["response_format", "object", "text", "JSON libre o validado por schema."],
            ["stream", "boolean", "false", "Activa eventos SSE."]
          ]} />
        </section>

        <section className="public-docs-section" id="easy">
          <div className="public-docs-section-icon"><Sparkles /></div>
          <h2>Contrato Easy</h2>
          <p>Easy compone una solicitud profesional internamente, pero conserva parámetros legibles para asistentes con identidad y contexto administrado.</p>
          <CodeBlock language="bash">{easyCurl}</CodeBlock>
          <ParameterTable rows={[
            ["prompt", "string", "requerido", "Solicitud principal del usuario."],
            ["context", "string", "—", "Información de negocio o situación."],
            ["personality", "string", "—", "Tono y comportamiento del asistente."],
            ["name", "string", "AK", "Nombre visible del asistente."],
            ["genero / gender", "string", "neutral", "Identidad lingüística guiada."],
            ["lang", "string", "Español", "Idioma esperado."],
            ["mode", "string", "auto", "Modo de ejecución."],
            ["skills", "string[]", "[]", "Skills que se aplicarán."],
            ["images", "string[]", "[]", "URLs o data URLs de imágenes."],
            ["image_file_ids", "uuid[]", "[]", "Imágenes subidas previamente."],
            ["artifact_ids", "uuid[]", "[]", "Artefactos usados como contexto."],
            ["tools", "string[]", "[]", "Conectores disponibles."],
            ["settings.memory", "boolean", "true", "Guarda y reutiliza memoria."],
            ["settings.ui_blocks", "boolean", "true", "Permite bloques estructurados para la interfaz."],
            ["settings.response_format", "object", "text", "Formato estructurado opcional."]
          ]} />
        </section>

        <section className="public-docs-section" id="vision">
          <div className="public-docs-section-icon"><Eye /></div>
          <h2>Visión multimodal</h2>
          <p>AK no convierte la imagen en una descripción textual ficticia: valida su formato, tamaño y propiedad, la normaliza y construye las partes multimodales nativas de cada proveedor compatible. Admite hasta ocho imágenes por solicitud y 10 MiB por archivo.</p>
          <h3>1. URL pública</h3>
          <CodeBlock>{`{
  "messages": [{
    "role": "user",
    "content": [
      {"type":"text","text":"Describe el diagrama"},
      {"type":"image_url","image_url":{"url":"https://example.com/diagram.png","detail":"high"}}
    ]
  }]
}`}</CodeBlock>
          <h3>2. Archivo directo con multipart</h3>
          <CodeBlock language="bash">{`FILE=$(curl ${baseUrl}/api/v1/files/vision \\
  -H "Authorization: Bearer $AK_API_KEY" \\
  -F "file=@./captura.png")

# Usa el campo id devuelto:
{
  "messages": [{"role":"user","content":[
    {"type":"text","text":"Analiza esta captura"},
    {"type":"input_image","file_id":"UUID-DEVUELTO"}
  ]}]
}`}</CodeBlock>
          <h3>3. Base64 o bytes</h3>
          <CodeBlock>{`{
  "messages": [{"role":"user","content":[
    {"type":"text","text":"Extrae los datos visibles"},
    {"type":"input_image","mime_type":"image/png","image_base64":"iVBORw0KGgo..."}
  ]}]
}

// Alternativa binaria dentro de JSON (menos eficiente):
{"type":"input_image","mime_type":"image/png","image_bytes":[137,80,78,71,...]}`}</CodeBlock>
          <div className="public-docs-callout"><ShieldCheck size={18} /><div><strong>Recomendación</strong><p>Usa multipart + <code>file_id</code> para archivos grandes, URL para recursos públicos estables y base64 solo cuando tu transporte ya trabaja con JSON.</p></div></div>
        </section>

        <section className="public-docs-section" id="artifacts">
          <div className="public-docs-section-icon"><Braces /></div>
          <h2>Artefactos automáticos e iterables</h2>
          <p>AK persiste automáticamente salidas reutilizables que no son texto plano: bloques de código, JSON, tablas Markdown, Mermaid, gráficos, diagramas y bloques UI. Cada resultado obtiene un ID, procedencia, checksum y versión.</p>
          <p>Importa uno o varios artefactos en cualquier contrato con <code>artifact_ids</code>. Solo se cargan si pertenecen al usuario autenticado.</p>
          <CodeBlock>{`{
  "model": "ak-auto",
  "messages": [{"role":"user","content":"Corrige el código y crea una nueva versión"}],
  "artifact_ids": ["d9349f9e-...."]
}`}</CodeBlock>
          <Endpoint method="GET" path="/api/v1/artifacts/:id">Obtiene contenido, origen y metadata.</Endpoint>
          <Endpoint method="GET" path="/api/v1/artifacts/:id/versions">Historial inmutable de versiones.</Endpoint>
          <p>La respuesta incluye únicamente referencias a los artefactos ya persistidos; abre el recurso por ID para recuperar el contenido completo.</p>
        </section>

        <section className="public-docs-section" id="modes">
          <h2>Modos de ejecución</h2>
          <div className="public-docs-card-grid">
            <article><strong>auto</strong><p>Clasifica localmente la solicitud y elige el perfil apropiado.</p></article>
            <article><strong>fast</strong><p>Prioriza baja latencia y respuestas directas.</p></article>
            <article><strong>standard</strong><p>Equilibra velocidad, calidad y coste.</p></article>
            <article><strong>thinking</strong><p>Reserva más razonamiento para problemas complejos.</p></article>
            <article><strong>agentic</strong><p>Planifica y ejecuta herramientas hasta producir una respuesta final.</p></article>
          </div>
          <p>La disponibilidad depende del plan. Si solicitas un modo no incluido, la API devuelve <code>mode_not_available</code>.</p>
        </section>

        <section className="public-docs-section" id="routing">
          <h2>Orquestación de rutas</h2>
          <p>Usa aliases lógicos para no acoplar tu aplicación a un modelo físico:</p>
          <div className="public-docs-alias-list">
            <div><code>ak-auto</code><span>Selección automática.</span></div>
            <div><code>ak-fast</code><span>Perfil de baja latencia.</span></div>
            <div><code>ak-balanced</code><span>Perfil equilibrado.</span></div>
            <div><code>ak-reasoning</code><span>Perfil ligero con razonamiento.</span></div>
            <div><code>ak-free</code><span>Pool gratuito cuando esté disponible.</span></div>
          </div>
          <p>AK evalúa prioridad, salud, circuit breaker, credenciales y compatibilidad. Los fallos temporales ponen una ruta o clave en cuarentena antes de probar el siguiente destino compatible.</p>
        </section>

        <section className="public-docs-section" id="memory">
          <div className="public-docs-section-icon"><Database /></div>
          <h2>Memoria persistente</h2>
          <p>Cuando la memoria está activa, la primera respuesta devuelve un <code>thread_id</code>. Envíalo en las solicitudes posteriores para continuar el mismo contexto.</p>
          <CodeBlock>{`{
  "model": "ak-auto",
  "input": "Continúa desde el plan anterior",
  "thread_id": "80e92f1a-...",
  "metadata": {
    "memory": true
  }
}`}</CodeBlock>
          <ul>
            <li>Los threads y mensajes están aislados por usuario.</li>
            <li>Una cuenta no puede leer ni continuar memoria de otra cuenta.</li>
            <li>El límite de registros y mensajes de contexto depende del plan.</li>
            <li>Archivar una memoria evita que vuelva a aparecer en el catálogo activo.</li>
            <li>Studio conserva el identificador en la URL para sobrevivir a recargas.</li>
          </ul>
        </section>

        <section className="public-docs-section" id="streaming">
          <h2>Streaming SSE</h2>
          <p>Establece <code>stream: true</code>. El contrato nativo envía eventos <code>delta</code>, <code>done</code>, <code>error</code> y heartbeats. Chat Completions emite chunks compatibles y termina con <code>[DONE]</code>.</p>
          <CodeBlock language="text">{`event: delta
data: {"delta":"Respuesta parcial"}

event: done
data: {"response":{"object":"ak.response","output_text":"..."}}

data: [DONE]`}</CodeBlock>
          <p>Si el cliente cierra la conexión, AK cancela la solicitud aguas arriba siempre que el proveedor lo permita.</p>
        </section>

        <section className="public-docs-section" id="structured">
          <h2>JSON estructurado</h2>
          <p>Usa <code>json_object</code> para un objeto libre o proporciona un JSON Schema. AK valida el resultado y realiza una corrección controlada si la salida no cumple el contrato.</p>
          <CodeBlock>{structuredExample}</CodeBlock>
        </section>

        <section className="public-docs-section" id="skills">
          <div className="public-docs-section-icon"><Sparkles /></div>
          <h2>System prompts, Skills básicas y Agent Skills</h2>
          <p>Un <strong>system prompt / Skill básica</strong> es una instrucción corta guardada en un único campo. Una <strong>Agent Skill</strong> es un paquete: <code>SKILL.md</code> con frontmatter, instrucciones y recursos opcionales bajo <code>references/</code>, <code>assets/</code>, <code>scripts/</code> y pruebas. AK carga solo los paquetes seleccionados, manteniendo pequeño el contexto global.</p>
          <CodeBlock>{`{
  "input": {
    "project": "Migración ERP",
    "findings": ["Retraso de dos semanas", "Riesgo en datos maestros"]
  },
  "skills": ["executive-summary", "checklist"],
  "mode": "standard"
}`}</CodeBlock>
          <Endpoint method="GET" path="/api/v1/skills">Devuelve skills de sistema, públicas y privadas visibles.</Endpoint>
          <Endpoint method="GET" path="/api/v1/agent-skills">Lista paquetes revisados o borradores visibles.</Endpoint>
          <Endpoint method="GET" path="/api/v1/skill-library">Catálogo multifuente con procedencia, licencia y confianza diferenciada.</Endpoint>
          <Endpoint method="GET" path="/api/v1/skill-library/inspect?source_url=...">Previsualiza SKILL.md, referencias, assets, pruebas y scripts antes de importar.</Endpoint>
          <Endpoint method="GET" path="/api/v1/agent-skills/:id">Inspecciona el paquete instalado y el contenido de todos sus archivos.</Endpoint>
          <Endpoint method="POST" path="/api/v1/agent-skills/import">Importa desde GitHub o desde un bundle propio.</Endpoint>
          <Endpoint method="POST" path="/api/v1/agent-skills/:id/review">Analiza secretos, instrucciones sospechosas y scripts; activa solo si no hay bloqueadores.</Endpoint>
          <Endpoint method="GET" path="/api/v1/agent-skills/:id/export">Exporta un bundle portable <code>.akskill.json</code>.</Endpoint>
          <p>Los scripts importados se conservan como recursos no ejecutables. Esto permite inspeccionarlos y exportarlos sin dar ejecución arbitraria al servidor. Las Skills de comunidad empiezan desactivadas y requieren revisión; las inexistentes, bloqueadas o no permitidas nunca se activan silenciosamente.</p>
          <p>En Studio abre <strong>Probar Skills</strong>, combina System prompts y Agent Skills activas, escribe un caso real y revisa la Skill aplicada en la respuesta. También puedes abrir Studio desde el botón <strong>Probar</strong> de cada tarjeta.</p>
        </section>

        <section className="public-docs-section" id="connectors">
          <div className="public-docs-section-icon"><Wrench /></div>
          <h2>Herramientas y conectores</h2>
          <p>Los conectores aportan datos externos o transformaciones. La API key necesita <code>tools:read</code> para ver el catálogo y <code>tools:execute</code> para ejecutarlos.</p>
          <Endpoint method="GET" path="/api/v1/connectors">Catálogo filtrado según el plan.</Endpoint>
          <Endpoint method="POST" path="/api/v1/connectors/:id/run">Ejecuta directamente un conector con un cuerpo JSON.</Endpoint>
          <h3>Uso desde una solicitud de IA</h3>
          <p>Envía el identificador en <code>tools</code> y usa <code>mode: "agentic"</code> o <code>tool_choice: "required"</code>. AK entrega al planificador la descripción y el esquema de entrada, el modelo decide cuándo llamarlo y el resultado vuelve al contexto antes de redactar la respuesta final.</p>
          <CodeBlock>{`{
  "model": "ak-auto",
  "mode": "agentic",
  "messages": [{"role":"user","content":"Consulta el estado de mi servicio"}],
  "tools": ["user:UUID-DEL-CONECTOR"],
  "tool_choice": "auto"
}`}</CodeBlock>
          <h3>Conectores personales Premium</h3>
          <ul>
            <li><strong>HTTP:</strong> URL pública o privada autorizada, método, headers y body con variables.</li>
            <li><strong>MySQL:</strong> consulta remota de solo lectura, preparada y limitada a 100 filas.</li>
            <li><strong>Prompt:</strong> genera una respuesta reutilizable o un system prompt.</li>
          </ul>
          <div className="public-docs-callout"><ShieldCheck size={18} /><div><strong>Acceso privado controlado</strong><p>La red privada se habilita por conector. RFC1918/ULA puede autorizarse, mientras loopback, link-local, multicast, metadatos, escrituras SQL y respuestas excesivas permanecen bloqueados.</p></div></div>
          <p>Los <strong>conectores del sistema</strong> tienen destinos definidos por AK y pueden resolver a la red privada compartida del VPS. Los lectores de URL arbitraria siguen exigiendo destinos públicos; loopback, link-local, multicast y endpoints de metadatos permanecen bloqueados.</p>
        </section>

        <section className="public-docs-section" id="generations">
          <h2>Generaciones creadas por el usuario o por AK</h2>
          <p>Generaciones admite únicamente <strong>imagen</strong>, <strong>documento</strong> y <strong>audio TTS</strong>. Música y video no forman parte del contrato. El audio se genera dentro del worker con el paquete npm <code>text2wav</code>, sin ejecutables del sistema y sin enviar el texto a una API TTS externa.</p>
          <CodeBlock>{`{
  "type": "audio",
  "prompt": "Bienvenido al reporte trimestral",
  "options": {"voice":"es", "rate":165}
}`}</CodeBlock>
          <Endpoint method="POST" path="/api/v1/jobs">Crea un trabajo manual.</Endpoint>
          <p>En modo agéntico, si el usuario pide explícitamente un resultado generado, AK puede poner el trabajo en cola por sí mismo. La respuesta nativa lo expone en <code>generations</code> y puede consultarse en Generaciones o con <code>GET /api/v1/jobs/:id</code>.</p>
        </section>

        <section className="public-docs-section" id="agentic">
          <h2>Modo agéntico</h2>
          <p>El agente recibe el catálogo permitido, propone un paso, ejecuta la herramienta y añade la observación al contexto. Continúa hasta terminar o alcanzar una guarda operativa configurable; no está limitado a cuatro pasos.</p>
          <CodeBlock>{agenticExample}</CodeBlock>
          <p>El motor evita repetir la misma herramienta con exactamente la misma entrada, conserva URLs para citas y registra latencia, estado y resultado de cada llamada.</p>
        </section>

        <section className="public-docs-section" id="byok">
          <div className="public-docs-section-icon"><KeyRound /></div>
          <h2>Claves propias de proveedores — BYOK</h2>
          <p>Desde Configuración puedes guardar claves de OpenAI, Gemini, OpenRouter, Groq, Cohere y otros proveedores compatibles. AK las cifra con AES-256-GCM y el secreto nunca vuelve al navegador.</p>
          <div className="public-docs-card-grid">
            <article><strong>Free</strong><p>Una clave por proveedor.</p></article>
            <article><strong>Pro</strong><p>Hasta cinco claves por proveedor.</p></article>
            <article><strong>Premium</strong><p>Claves por proveedor sin límite de plan.</p></article>
          </div>
          <p>Si una solicitud se resuelve con BYOK, devuelve <code>billing_source: "byok"</code> y consume cero créditos AK. Las cuotas y restricciones pasan a ser las de esa cuenta externa.</p>
          <p>Si falla una clave propia aplicable, AK no cambia silenciosamente a una clave financiada por el sistema.</p>
        </section>

        <section className="public-docs-section" id="custom-models">
          <div className="public-docs-section-icon"><Network /></div>
          <h2>Modelos personalizados mediante HTTP</h2>
          <p>Configuración permite registrar cualquier API de IA GET o POST como una ruta del usuario. Cada modelo recibe un alias <code>custom:slug</code>, puede activarse o desactivarse y participa en Fast, Balanced o Reasoning según el perfil elegido.</p>
          <CodeBlock>{`{
  "history": {{messages}}
}`}</CodeBlock>
          <p>Cuando una variable ocupa todo el valor —por ejemplo <code>{"{{messages}}"}</code>— AK inserta el array real. También están disponibles <code>{"{{history}}"}</code>, <code>{"{{prompt}}"}</code>, <code>{"{{system}}"}</code>, <code>{"{{temperature}}"}</code> y <code>{"{{max_tokens}}"}</code>.</p>
          <p>Indica una ruta de respuesta como <code>response</code>, <code>result.answer</code> o <code>choices.0.message.content</code>. Si queda vacía, AK reconoce formatos habituales. Puedes seleccionar el alias directamente:</p>
          <CodeBlock>{`{
  "model": "custom:modelo-personal",
  "messages": [{"role":"user","content":"Hola"}]
}`}</CodeBlock>
          <Endpoint method="GET" path="/api/v1/user-model-endpoints">Lista modelos HTTP de la cuenta y sus métricas.</Endpoint>
          <Endpoint method="POST" path="/api/v1/user-model-endpoints/:id/test">Prueba el contrato guardado sin pasar por Studio.</Endpoint>
        </section>

        <section className="public-docs-section" id="response">
          <h2>Respuesta nativa</h2>
          <CodeBlock>{responseExample}</CodeBlock>
          <ParameterTable rows={[
            ["output_text", "string", "—", "Texto final normalizado."],
            ["provider", "string", "—", "Pool que atendió la solicitud."],
            ["model", "string", "—", "Destino físico usado; no lo acoples a tu aplicación."],
            ["thread_id", "uuid | null", "null", "Memoria creada o continuada."],
            ["usage", "object", "—", "Tokens, créditos y fuente de facturación."],
            ["tool_calls", "array", "[]", "Pasos de herramientas ejecutados."],
            ["ui_blocks", "array", "[]", "Bloques estructurados para interfaces."],
            ["artifacts", "array", "[]", "Artefactos persistidos."],
            ["generations", "array", "[]", "Trabajos de imagen, documento o TTS creados por el agente."],
            ["citations", "array", "[]", "Referencias obtenidas por conectores."],
            ["latency_ms", "integer", "—", "Tiempo total observado por AK."],
            ["request_id", "string", "—", "Identificador para trazabilidad y soporte."]
          ]} />
        </section>

        <section className="public-docs-section" id="errors">
          <h2>Errores</h2>
          <p>Todos los errores contienen un código estable, mensaje legible y <code>request_id</code>.</p>
          <CodeBlock>{errorExample}</CodeBlock>
          <div className="public-docs-error-list">
            <div><code>400 validation_error</code><span>El cuerpo no cumple el contrato.</span></div>
            <div><code>401 authentication_required</code><span>Falta una credencial válida.</span></div>
            <div><code>403 insufficient_scope</code><span>La API key no autoriza la operación.</span></div>
            <div><code>403 feature_not_available</code><span>El plan no incluye la capacidad.</span></div>
            <div><code>404 not_found</code><span>El recurso no existe o no pertenece al usuario.</span></div>
            <div><code>429 credits_exhausted</code><span>Se agotaron los créditos del sistema.</span></div>
            <div><code>502 provider_failed</code><span>Fallaron las rutas compatibles.</span></div>
            <div><code>502 byok_provider_failed</code><span>Fallaron las claves propias aplicables.</span></div>
            <div><code>503 connector_unavailable</code><span>Una herramienta está deshabilitada o en cuarentena.</span></div>
          </div>
        </section>

        <section className="public-docs-section" id="scopes">
          <h2>Scopes de API keys AK</h2>
          <div className="public-docs-scope-list">
            <div><code>ai:read</code><span>Rutas, uso y memoria.</span></div>
            <div><code>ai:write</code><span>Respuestas y Chat Completions.</span></div>
            <div><code>tools:read</code><span>Catálogo de herramientas.</span></div>
            <div><code>tools:execute</code><span>Ejecución directa y agéntica.</span></div>
            <div><code>jobs:read</code><span>Consulta de generaciones.</span></div>
            <div><code>jobs:write</code><span>Creación, cancelación y reintentos.</span></div>
            <div><code>artifacts:read</code><span>Lectura de artefactos.</span></div>
            <div><code>artifacts:write</code><span>Creación y edición de artefactos.</span></div>
          </div>
        </section>

        <section className="public-docs-section" id="limits">
          <div className="public-docs-section-icon"><ShieldCheck /></div>
          <h2>Límites, seguridad y operación</h2>
          <ul>
            <li>Rate limiting por usuario autenticado o dirección IP.</li>
            <li>Límite de cuerpo JSON y tiempos máximos por proveedor o herramienta.</li>
            <li>Circuit breakers para rutas, credenciales y conectores.</li>
            <li>Protección CSRF para operaciones web con sesión.</li>
            <li>API keys AK hasheadas y secretos BYOK cifrados.</li>
            <li>Aislamiento de memoria, artefactos, jobs y Skills por perfil, workspace y proyecto; credenciales privadas por usuario.</li>
            <li>Validación SSRF y tamaño máximo para recursos externos.</li>
            <li>Colas MySQL durables para jobs y notificaciones importantes.</li>
            <li><code>Idempotency-Key</code> en operaciones POST/PUT/PATCH para reintentos seguros durante 24 horas.</li>
            <li>Request ID, Server-Timing, métricas Prometheus protegidas y telemetría por ruta.</li>
          </ul>
          <div className="public-docs-callout success"><Activity size={18} /><div><strong>Estado del servicio</strong><p>Consulta disponibilidad pública y latencia en <a href="/status">api.akiomae.xyz/status</a>.</p></div></div>
        </section>

        <section className="public-docs-section" id="operations">
          <div className="public-docs-section-icon"><Activity /></div>
          <h2>Webhooks, workspaces, OpenAPI y SDKs</h2>
          <p>Operaciones incorpora workspaces con roles Owner/Admin/Developer/Viewer, proyectos y presupuesto mensual, exportación de cuenta y un centro de seguridad para sesiones y credenciales.</p>
          <h3>Contexto de organización y proyecto</h3>
          <p>Selecciona el workspace y proyecto desde la cabecera del dashboard. En integraciones externas envía los mismos identificadores mediante headers; AK verificará la membresía y aplicará el aislamiento a memoria, Skills, artefactos, generaciones y consumo.</p>
          <CodeBlock language="http">{`X-AK-Workspace: 7f6c2c8e-...\nX-AK-Project: 13cd773d-...`}</CodeBlock>
          <Endpoint method="GET" path="/api/v1/workspaces">Lista las organizaciones donde participa la cuenta.</Endpoint>
          <Endpoint method="GET" path="/api/v1/workspaces/:workspaceId">Perfil, capacidades, miembros, proyectos y métricas.</Endpoint>
          <Endpoint method="POST" path="/api/v1/workspaces/:workspaceId/members">Añade un usuario registrado con rol Admin, Developer o Viewer.</Endpoint>
          <Endpoint method="PATCH" path="/api/v1/workspaces/:workspaceId/members/:userId">Actualiza el rol respetando la jerarquía del workspace.</Endpoint>
          <Endpoint method="POST" path="/api/v1/workspaces/:workspaceId/projects">Crea un proyecto con descripción y presupuesto opcional.</Endpoint>
          <Endpoint method="PATCH" path="/api/v1/workspaces/:workspaceId/projects/:projectId">Edita, archiva o restaura un proyecto.</Endpoint>
          <h3>Webhooks firmados</h3>
          <p>Los eventos <code>ai.response.completed</code>, <code>artifact.created</code> y <code>generation.queued</code> se entregan por HTTPS. El worker reintenta hasta cinco veces. Verifica <code>X-AK-Signature</code> calculando HMAC-SHA256 sobre <code>timestamp.payload_crudo</code> con el secreto mostrado al crear el endpoint.</p>
          <Endpoint method="GET" path="/api/v1/webhooks">Catálogo de endpoints, eventos y entregas.</Endpoint>
          <Endpoint method="POST" path="/api/v1/webhooks">Crea un endpoint y devuelve su secreto una sola vez.</Endpoint>
          <h3>Contrato y clientes</h3>
          <Endpoint method="GET" path="/openapi.json">Especificación OpenAPI 3.1.</Endpoint>
          <Endpoint method="GET" path="/sdks/javascript">Cliente JavaScript mínimo.</Endpoint>
          <Endpoint method="GET" path="/sdks/python">Cliente Python mínimo.</Endpoint>
          <CodeBlock language="http">{`Idempotency-Key: order_2026_08_21_001
X-Request-Id: tu-id-opcional`}</CodeBlock>
        </section>

        <footer className="public-docs-footer">
          <div><DocsBrand /><p>Documentación de integración para AK-v5.0.</p></div>
          <div><a href="/status">Estado</a><a href="/auth/google">Dashboard</a><a href="https://discord.gg/PwXEZCFBjY" target="_blank" rel="noreferrer">Discord <ExternalLink size={13} /></a></div>
        </footer>
      </main>
    </div>
  );
}
